<template>
  <div>
    <v-row align="center" class="mb-2">
      <v-col cols="12" md="6">
        <v-btn color="primary" @click="showSend = true" v-if="$can('do', 'POKO_Podpisy')">
          <v-icon left small>fa-tablet-alt</v-icon> Wyślij na tablet
        </v-btn>
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-switch v-model="includeHistory" label="Pokaż odwołane i zastąpione" dense hide-details class="d-inline-block mr-4" @change="load"></v-switch>
        <v-btn icon @click="load" title="Odśwież"><v-icon small>fa-sync</v-icon></v-btn>
      </v-col>
    </v-row>

    <v-alert type="info" dense text v-if="activeSessions.length">
      Na tablecie <b>{{ activeSessions[0].terminalCode }}</b> trwa sesja: {{ statusLabel(activeSessions[0].status) }}
      <v-btn small text color="primary" class="ml-2" @click="openSession(activeSessions[0].sessionId)">Podgląd</v-btn>
    </v-alert>

    <v-data-table :items="documents" :headers="headers" :loading="loading" hide-default-footer disable-pagination
      no-data-text="Brak podpisanych dokumentów">
      <template v-slot:item.title="{ item }">
        <div>{{ item.title }}</div>
        <div class="grey--text caption">{{ item.templateCode }} v{{ item.templateVersion }} · {{ item.documentNumber }}</div>
      </template>
      <template v-slot:item.signedAt="{ item }">{{ formatDate(item.signedAt) }}</template>
      <template v-slot:item.signers="{ item }">
        <div v-for="s in item.signers" :key="s.sequenceNo">{{ s.displayName }} <span class="grey--text">({{ roleLabel(s.role) }})</span></div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="docColor(item.status)" dark>{{ docLabel(item.status) }}</v-chip>
        <v-chip small class="ml-1" v-if="item.revokedAt" color="red" outlined>odwołany {{ formatDate(item.revokedAt) }}</v-chip>
        <v-chip small class="ml-1" v-else-if="item.supersededByDocumentId" color="grey" outlined>zastąpiony</v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" v-if="item.pdfFingerprint" @click="viewPdf(item)" title="Wyświetl PDF">fa-file-pdf</v-icon>
        <v-icon small class="mr-2" @click="showDetails(item)" title="Szczegóły i audyt">fa-info-circle</v-icon>
        <v-icon small class="mr-2" v-if="item.isCurrent && $can('do', 'POKO_Podpisy_Odwolanie')" @click="revoke(item)" title="Odwołaj">fa-ban</v-icon>
      </template>
    </v-data-table>

    <v-expansion-panels class="mt-4" flat v-if="sessions.length">
      <v-expansion-panel>
        <v-expansion-panel-header>Historia sesji podpisu ({{ sessions.length }})</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-simple-table dense>
            <thead><tr><th>Data</th><th>Dokument</th><th>Tablet</th><th>Status</th><th>Nr dokumentu</th><th></th></tr></thead>
            <tbody>
              <tr v-for="s in sessions" :key="s.sessionId">
                <td>{{ formatDateTime(s.createdAt) }}</td>
                <td>{{ s.templateTitle }}</td>
                <td>{{ s.terminalCode }}</td>
                <td><v-chip x-small :color="sessionColor(s.status)" dark>{{ statusLabel(s.status) }}</v-chip>
                  <span class="grey--text caption" v-if="s.cancelReason"> {{ cancelLabel(s.cancelReason) }}</span></td>
                <td>{{ s.documentNumber }}</td>
                <td><v-icon small @click="openSession(s.sessionId)" title="Szczegóły">fa-info-circle</v-icon></td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <SendToTabletDialog v-if="showSend" :visible="showSend" :clientId="client" :clientName="clientName"
      :excludeTemplateCodes="fromVisit ? excludedCodes : []" @close="onSendClosed"></SendToTabletDialog>

    <v-dialog v-model="showSessionDialog" max-width="720px">
      <v-card>
        <v-toolbar dark class="primary" dense>
          <v-btn icon @click="showSessionDialog = false" dark><v-icon>fa-times</v-icon></v-btn>
          <v-toolbar-title>Sesja podpisu</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <SigningSessionStatus v-if="showSessionDialog && selectedSessionId" :sessionId="selectedSessionId" @finished="load"></SigningSessionStatus>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPdf" fullscreen persistent>
      <v-card flat tile class="d-flex flex-column" style="height: 100vh">
        <v-toolbar dark class="primary" dense flat>
          <v-btn icon @click="closePdf" dark><v-icon>fa-times</v-icon></v-btn>
          <v-toolbar-title>{{ pdfTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-progress-circular indeterminate size="22" width="2" color="white" v-if="pdfLoading"></v-progress-circular>
        </v-toolbar>
        <div style="flex: 1 1 auto; overflow: hidden; background: #525659">
          <iframe v-if="pdfUrl" :src="pdfUrl" style="width: 100%; height: 100%; border: 0"></iframe>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDoc" max-width="820px">
      <v-card v-if="doc">
        <v-toolbar dark class="primary" dense>
          <v-btn icon @click="showDoc = false" dark><v-icon>fa-times</v-icon></v-btn>
          <v-toolbar-title>{{ doc.documentNumber }} · {{ doc.title }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <div class="caption grey--text">Hash treści: {{ doc.contentSha256 }}</div>
          <div class="caption grey--text" v-if="doc.pdfSha256">Hash PDF: {{ doc.pdfSha256 }}</div>
          <div class="caption red--text" v-if="doc.renderError">Błąd renderu: {{ doc.renderError }}</div>
          <pre class="mt-3 pa-3 grey lighten-4" style="white-space: pre-wrap; font-family: inherit">{{ doc.finalContent }}</pre>
          <v-simple-table dense class="mt-3">
            <tbody>
              <tr v-for="(a, i) in doc.audit" :key="i">
                <td style="width: 150px" class="grey--text">{{ formatDateTime(a.timestamp) }}</td>
                <td>{{ eventLabel(a.event) }}</td>
                <td class="grey--text caption">{{ a.metadata }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Zakładka "Podpisy" w karcie pacjenta: podpisane dokumenty (domyślnie tylko
// obowiązujące), wysyłka na tablet, historia sesji. PDF pobierany istniejącą
// ścieżką załączników (fingerprint -> token -> /file/DownloadFile).
import { podpisyService, podpisyLabels, abpError } from "@/services/podpisy.service";
import { clientService } from "@/services/";
import SendToTabletDialog from "./SendToTabletDialog";
import SigningSessionStatus from "./SigningSessionStatus";
import { mapActions } from "vuex";
import swal from "sweetalert";
import dayjs from "dayjs";

export default {
  name: "ClientSignatures",
  components: { SendToTabletDialog, SigningSessionStatus },
  props: ["client", "clientName", "fromVisit"],
  data() {
    return {
      loading: false,
      documents: [],
      sessions: [],
      includeHistory: false,
      showSend: false,
      showSessionDialog: false,
      selectedSessionId: null,
      showDoc: false,
      doc: null,
      showPdf: false,
      pdfUrl: null,
      pdfTitle: "",
      pdfLoading: false,
      headers: [
        { text: "Dokument", value: "title", sortable: false },
        { text: "Podpisano", value: "signedAt", sortable: false },
        { text: "Podpisujący", value: "signers", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],
    };
  },
  computed: {
    activeSessions() {
      return this.sessions.filter(s => ["WAITING", "OPENED", "CONFIRMED"].indexOf(s.status) >= 0);
    },
    // Kody szablonów, których NIE wolno ponownie wysłać od strony wizyty:
    // dokument już obowiązujący (podpisany, nieodwołany) lub sesja w toku.
    excludedCodes() {
      const signed = this.documents.filter(d => d.isCurrent && !d.revokedAt).map(d => d.templateCode);
      const inProgress = this.activeSessions.map(s => s.templateCode);
      return Array.from(new Set(signed.concat(inProgress).filter(Boolean)));
    },
  },
  methods: {
    ...mapActions("alert", ["error", "success"]),
    formatDate(v) { return v ? dayjs(v).format("DD-MM-YYYY") : ""; },
    formatDateTime(v) { return v ? dayjs(v).format("DD-MM-YYYY HH:mm") : ""; },
    roleLabel(r) { return podpisyLabels.signerRole[r] || r; },
    docLabel(s) { return podpisyLabels.documentStatus[s] || s; },
    docColor(s) { return podpisyLabels.documentColor[s] || "grey"; },
    statusLabel(s) { return podpisyLabels.sessionStatus[s] || s; },
    sessionColor(s) { return podpisyLabels.sessionColor[s] || "grey"; },
    cancelLabel(r) { return podpisyLabels.cancelReason[r] || r; },
    eventLabel(e) { return podpisyLabels.event[e] || e; },
    load() {
      this.loading = true;
      Promise.all([
        podpisyService.getDocumentsForClient(this.client, this.includeHistory),
        podpisyService.getSessionsForClient(this.client),
      ]).then(([d, s]) => {
        this.documents = d.data.result;
        this.sessions = s.data.result;
      }).catch(e => this.error(abpError(e, "Nie można pobrać podpisanych dokumentów")))
        .finally(() => { this.loading = false; });
    },
    onSendClosed() {
      this.showSend = false;
      this.load();
    },
    openSession(id) {
      this.selectedSessionId = id;
      this.showSessionDialog = true;
    },
    showDetails(item) {
      podpisyService.getDocument(item.id).then(resp => {
        this.doc = resp.data.result;
        this.showDoc = true;
      }).catch(e => this.error(abpError(e, "Nie można pobrać dokumentu")));
    },
    // Podgląd podpisanego PDF na ekranie (bez zapisu na dysk) - pełnoekranowe okno z iframe.
    viewPdf(item) {
      this.pdfTitle = item.documentNumber + " · " + item.title;
      this.pdfLoading = true;
      this.showPdf = true;
      clientService.preDownloadAttachment(item.pdfFingerprint).then(response => {
        const pokoFile = response.data.result;
        return clientService.downloadAttachment(pokoFile.token).then(resp => {
          const blob = new Blob([resp.data], { type: pokoFile.mime_type || "application/pdf" });
          if (this.pdfUrl) window.URL.revokeObjectURL(this.pdfUrl);
          this.pdfUrl = window.URL.createObjectURL(blob);
        });
      }).catch(() => {
        this.showPdf = false;
        this.error("Błąd wczytywania PDF");
      }).finally(() => { this.pdfLoading = false; });
    },
    closePdf() {
      this.showPdf = false;
      if (this.pdfUrl) { window.URL.revokeObjectURL(this.pdfUrl); this.pdfUrl = null; }
      this.pdfTitle = "";
    },
    revoke(item) {
      swal({
        title: "Odwołać dokument?",
        text: item.documentNumber + " · " + item.title + "\nOryginał zostaje w archiwum z adnotacją o odwołaniu.",
        icon: "warning", buttons: ["Nie", "Odwołaj"], dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return;
        podpisyService.revokeDocument(item.id, "Odwołanie z karty pacjenta").then(() => {
          this.success("Dokument odwołany");
          this.load();
        }).catch(e => this.error(abpError(e, "Nie można odwołać dokumentu")));
      });
    },
  },
  mounted() {
    this.load();
  },
};
</script>
